<template>
  <Dialog :initialFocus="initialFocusRef" :show="true" class="fixed inset-0 z-50 flex items-center justify-center">
    <DialogOverlay class="fixed inset-0 bg-black/50" />

    <DialogPanel
      :theme="theme"
      :high-contrast="highAccessibility"
      class="flex w-full max-w-3xl flex-col items-center gap-6 p-12 text-center"
      ref="initialFocusRef"
    >
      <DialogTitle class="text-2xl font-bold sm:text-[2.5rem]" tabindex="-1">
        {{ t("msg.youAreBeingRedirected") }}
        <div class="font-semibold">{{ displayRoute }}</div>
      </DialogTitle>

      <DialogDescription class="text-xl text-gray-700 sm:text-2xl">
        Redirecting in {{ remaining }} second{{ remaining !== 1 ? "s" : "" }}...
      </DialogDescription>

      <ThemedButton aria-label="Stop redirecting" type="button" variant="outline" @click="cancelRedirect">
        <XCircleIcon class="h-4 w-4 md:h-5 md:w-5" />
        {{ t("msg.dontWantToBeRedirected") }}
      </ThemedButton>
      <ThemedButton aria-label="Send to redirect target now" type="button" @click="executeRedirect">
        {{ t("msg.redirectNow") }}
        <PaperAirplaneIcon class="h-4 w-4 md:h-5 md:w-5" />
      </ThemedButton>
    </DialogPanel>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useThemeStore } from "../pinia/theme.ts";
import DialogPanel from "./ui/dialog/DialogPanel.vue";
import Dialog from "./ui/dialog/Dialog.vue";
import DialogOverlay from "./ui/dialog/DialogOverlay.vue";
import DialogTitle from "./ui/dialog/DialogTitle.vue";
import { DialogDescription } from "@headlessui/vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { isNativePlatform } from "../capacitor/platform";

const isNative = isNativePlatform();
import { PaperAirplaneIcon, XCircleIcon } from "@heroicons/vue/24/outline";
import ThemedButton from "../user/components/ThemedButton.vue";

const { t } = useI18n();

// Props
const props = defineProps({
  seconds: { type: Number, required: true },
  to: { type: String, required: true },
  external: { type: Boolean, default: false },
});

// Emits
const emit = defineEmits<{
  (e: "cancel"): void;
}>();

const router = useRouter();
const themeStore = useThemeStore();
themeStore.fetchTheme();

const theme = computed(() => themeStore.accent);
const highAccessibility = computed(() => themeStore.highAccessibility);

const remaining = ref(props.seconds);
let timer: ReturnType<typeof setInterval> | null = null;
const displayRoute = ref("");
const initialFocusRef = ref(null);

function isValidUrl(): boolean {
  if (props.external) {
    try {
      const url = new URL(props.to);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  }
  try {
    router.resolve(props.to);
    return true;
  } catch {
    return false;
  }
}

function getDisplayRoute(): string {
  if (props.external) {
    try {
      return new URL(props.to).hostname;
    } catch {
      return props.to;
    }
  } else {
    const resolved = router.resolve(props.to);
    return resolved.meta?.title ?? resolved.path;
  }
}

onMounted(() => {
  if (!isValidUrl()) {
    console.error("Invalid redirect target:", props.to);
    return;
  }

  displayRoute.value = getDisplayRoute();
  initialFocusRef.value?.focus();

  timer = setInterval(() => {
    if (remaining.value > 1) {
      remaining.value--;
      return;
    }

    executeRedirect();
  }, 1000);
});

onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});

async function executeRedirect() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }

  if (props.external) {
    if (isNative) {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url: props.to });
    } else {
      window.location.assign(props.to);
    }
  } else {
    router.push(props.to);
  }
}

function cancelRedirect() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  remaining.value = 0;
  displayRoute.value = "Cancelled";

  emit("cancel");
}
</script>

<style scoped>
.dialog-enter-active,
.dialog-leave-active {
  transition: opacity 0.3s ease;
}
.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}
</style>
